// (C) Copyright 2025 Hewlett Packard Enterprise Development LP
import { Trans } from 'react-i18next'
import ReactDOM from 'react-dom/client'

import { Notification, Anchor } from '../../components'
import { isServiceCentric } from '../../utils/account-utils'
import { isGLOP } from '../../utils/feature-flag-utils'

import { GlobalSearch } from './global-search/GlobalSearch'
import { updateHeader } from './header-cases/updateHeader'

export const getErrorBanner = ({
  errorBanner,
  setErrorBanner,
  navigate,
  t
}) => {
  const bannerMessage = (
    <Trans i18nKey={errorBanner.message} t={t}>
      <Anchor
        style={{ whiteSpace: 'wrap' }}
        weight="normal"
        onClick={() =>
          navigate('/manage-account/auditlogs', {
            state: errorBanner?.name
          })
        }
      />
      <Anchor
        style={{ whiteSpace: 'wrap' }}
        weight="normal"
        href={errorBanner.href}
        target="_blank"
      />
    </Trans>
  )
  return (
    <Notification
      global
      background="status-critical"
      onClose={() => setErrorBanner(null)}
      message={bannerMessage}
      data-testid="error-banner-notification"
    />
  )
}

export const getGlobalHeaderLeftChildElement = () => {
  const headerRootNode = document?.querySelector('greenlake-header')
  const innerChildElement = headerRootNode.shadowRoot?.querySelector('.inner')
  const leftChildElement = innerChildElement?.querySelector('.left')
  return leftChildElement
}

export const removeGlobalSearchBar = () => {
  const leftChildElement = getGlobalHeaderLeftChildElement()
  if (leftChildElement) {
    const searchBar = leftChildElement?.querySelector('.searchbar-container')
    if (searchBar) {
      searchBar.remove()
    }
  }
}

export const addGlobalSearchBar = ({
  LDFlags,
  ld,
  custAccountLoaded,
  oidcUser,
  dispatchVisibilityContext,
  dispatchUPSContext,
  dispatchCCSContext,
  csrfToken,
  t,
  logger,
  navigate
}) => {
  const leftChildElement = getGlobalHeaderLeftChildElement()
  if (leftChildElement) {
    if (
      LDFlags['glcp-global-search-jhansi'] &&
      custAccountLoaded?.company_name &&
      !leftChildElement?.querySelector('.searchbar-container')
    ) {
      const div = document.createElement('div')
      div.className = 'searchbar-container'
      leftChildElement.appendChild(div)
      const root = ReactDOM.createRoot(leftChildElement.appendChild(div))
      root.render(
        <GlobalSearch
          oidcUser={oidcUser}
          i18nTranslation={t}
          LDFlags={LDFlags}
          logger={logger}
          dispatchVisibilityContext={dispatchVisibilityContext}
          dispatchUPSContext={dispatchUPSContext}
          navigate={navigate}
          ld={ld}
          dispatchCCSContext={dispatchCCSContext}
          csrfToken={csrfToken}
        />
      )
    } else if (
      !LDFlags['glcp-global-search-jhansi'] &&
      leftChildElement?.querySelector('.searchbar-container')
    ) {
      leftChildElement?.querySelector('.searchbar-container').remove()
    }
  }
}

export const updateHeaderFlags = ({
  LDFlags,
  custAccountLoaded,
  isSupportEngineer,
  hideHeaderOptions,
  oidcUser,
  rbacPolicies,
  showWCHeader
}) => {
  const headerRootNode = document.querySelector('greenlake-header')
  if (headerRootNode) {
    headerRootNode.ldFlags = {
      ...LDFlags,
      'glcp-service-centric-experience-phase-1':
        isServiceCentric(LDFlags) || isGLOP(),
      'glcp-switch-to-workspace':
        LDFlags['glcp-switch-to-workspace'] || isGLOP(),
      'glcp-wc-header': LDFlags['glcp-wc-header'] || isGLOP(),
      'glcp-support-access-manager':
        custAccountLoaded &&
        !isSupportEngineer &&
        LDFlags['glcp-support-access-manager']
    }
    // Update header with new platform based on hideHeaderOptions
    updateHeader(hideHeaderOptions, oidcUser, rbacPolicies)
  }
  document.documentElement.style.setProperty(
    '--header-display',
    showWCHeader ? 'block' : 'none'
  )
}
